export function ProfessionalAppraisalProcess() {
  return (
    <section className="py-20 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Professional Appraisal Process
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            While the quick valuation methods provide useful estimates, a professional appraisal offers the most comprehensive and defensible property valuation. Understanding the appraisal process helps you prepare and ensures accurate results.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">When You Need a Professional Appraisal</h3>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Professional appraisals are required for most financing transactions and provide the most accurate, unbiased property valuations. Here's when you should obtain a formal appraisal:
            </p>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-bank-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Financing Requirements</h4>
                  <p className="text-gray-600">Lenders require appraisals for all mortgage transactions to verify property value</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-scales-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Legal Disputes</h4>
                  <p className="text-gray-600">Estate settlements, divorces, and partnership dissolutions require formal appraisals</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-file-text-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Tax Purposes</h4>
                  <p className="text-gray-600">Property tax appeals, estate taxes, and charitable donations need documented appraisals</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-shield-check-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Insurance Coverage</h4>
                  <p className="text-gray-600">Determining appropriate insurance coverage amounts for replacement cost</p>
                </div>
              </div>
            </div>
          </div>
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/282684206067a2a9aef9bcd421a51b35.jpg"
              alt="Professional apartment building appraisal process"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 lg:p-12 mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">The Appraisal Process Step-by-Step</h3>
          
          <div className="space-y-6">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-600 text-white rounded-full flex-shrink-0 text-2xl font-bold">
                1
              </div>
              <div className="flex-1">
                <h4 className="text-xl font-bold text-gray-900 mb-3">Property Inspection</h4>
                <p className="text-gray-700 leading-relaxed mb-3">
                  The appraiser conducts a thorough physical inspection of the property, measuring square footage, photographing units and common areas, and noting condition, amenities, and any defects or needed repairs.
                </p>
                <p className="text-gray-600 text-sm">Timeline: 2-4 hours for typical apartment building</p>
              </div>
            </div>

            <div className="flex items-start gap-6">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-600 text-white rounded-full flex-shrink-0 text-2xl font-bold">
                2
              </div>
              <div className="flex-1">
                <h4 className="text-xl font-bold text-gray-900 mb-3">Financial Analysis</h4>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Review of rent rolls, operating statements, lease agreements, and expense documentation. The appraiser analyzes income, expenses, and calculates Net Operating Income (NOI) for the income approach valuation.
                </p>
                <p className="text-gray-600 text-sm">Required documents: 3 years operating statements, current rent roll, lease agreements</p>
              </div>
            </div>

            <div className="flex items-start gap-6">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-600 text-white rounded-full flex-shrink-0 text-2xl font-bold">
                3
              </div>
              <div className="flex-1">
                <h4 className="text-xl font-bold text-gray-900 mb-3">Market Research</h4>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Research of comparable sales, rental rates, cap rates, and market conditions. The appraiser identifies 3-5 comparable properties and makes adjustments for differences in size, condition, location, and amenities.
                </p>
                <p className="text-gray-600 text-sm">Focus: Sales within past 6-12 months in same submarket</p>
              </div>
            </div>

            <div className="flex items-start gap-6">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-600 text-white rounded-full flex-shrink-0 text-2xl font-bold">
                4
              </div>
              <div className="flex-1">
                <h4 className="text-xl font-bold text-gray-900 mb-3">Valuation Analysis</h4>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Application of all three valuation approaches: income approach (primary), sales comparison approach (supporting), and cost approach (supporting). Each method provides a value indication that is reconciled into a final opinion of value.
                </p>
                <p className="text-gray-600 text-sm">Methods: Income approach weighted most heavily for apartment buildings</p>
              </div>
            </div>

            <div className="flex items-start gap-6">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-600 text-white rounded-full flex-shrink-0 text-2xl font-bold">
                5
              </div>
              <div className="flex-1">
                <h4 className="text-xl font-bold text-gray-900 mb-3">Report Preparation</h4>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Comprehensive written report documenting all findings, analysis, and conclusions. The report includes property description, market analysis, comparable sales data, valuation calculations, and final value conclusion with supporting rationale.
                </p>
                <p className="text-gray-600 text-sm">Deliverable: 40-80 page detailed appraisal report</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <img
              src="https://readdy.ai/api/search-image?query=detailed-appraisal-report-document-professional-property-valuation-report-comprehensive-analysis-financial-documentation-real-estate-appraisal-paperwork-official-valuation-document-professional-report&width=800&height=600&seq=appraisal-report-001&orientation=landscape"
              alt="Professional appraisal report documentation"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Appraisal Costs and Timeline</h3>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Professional apartment building appraisals are more complex and expensive than single-family appraisals due to the financial analysis required and complexity of income-producing properties.
            </p>
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-5 shadow-md">
                <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <i className="ri-money-dollar-circle-line text-teal-600"></i>
                  Typical Appraisal Costs
                </h4>
                <ul className="text-gray-600 space-y-1 ml-8">
                  <li>• 5-20 units: $2,500 - $4,000</li>
                  <li>• 21-50 units: $4,000 - $6,000</li>
                  <li>• 51-100 units: $6,000 - $10,000</li>
                  <li>• 100+ units: $10,000+</li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-5 shadow-md">
                <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <i className="ri-time-line text-teal-600"></i>
                  Expected Timeline
                </h4>
                <ul className="text-gray-600 space-y-1 ml-8">
                  <li>• Inspection: 1-2 days</li>
                  <li>• Research & analysis: 5-7 days</li>
                  <li>• Report writing: 3-5 days</li>
                  <li>• Total time: 10-14 business days</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}